import React, { useState, useEffect, useCallback } from 'react';
import { Check, AlertCircle, Loader2 } from 'lucide-react';
import { Input } from './Input';
import { productService } from '@services/productService';
import { clsx } from 'clsx';

type ValidationState = 'neutral' | 'validating' | 'available' | 'unavailable' | 'error';

interface SkuInputProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  onValidationChange?: (isValid: boolean, message?: string) => void;
  disabled?: boolean;
  placeholder?: string;
  maxLength?: number;
  isEditing?: boolean; // Para não validar o SKU atual durante edição
}

/**
 * Componente de input para SKU com validação em tempo real
 * Implementa debounce para evitar muitas requisições e feedback visual para o usuário
 */
export const SkuInput: React.FC<SkuInputProps> = ({
  label = "SKU *",
  value,
  onChange,
  error,
  onValidationChange,
  disabled,
  placeholder = "Ex: ABC123",
  maxLength = 50,
  isEditing = false
}) => {
  const [validationState, setValidationState] = useState<ValidationState>('neutral');
  const [validationMessage, setValidationMessage] = useState<string>('');
  const [validationTimeout, setValidationTimeout] = useState<number | null>(null);

  // Debounced validation function
  const validateSku = useCallback(async (sku: string) => {
    if (!sku.trim()) {
      setValidationState('neutral');
      setValidationMessage('');
      onValidationChange?.(false);
      return;
    }

    if (sku.length < 1) {
      const msg = 'SKU deve ter pelo menos 1 caractere';
      setValidationState('error');
      setValidationMessage(msg);
      onValidationChange?.(false, msg);
      return;
    }

    if (sku.length > maxLength) {
      const msg = `SKU deve ter no máximo ${maxLength} caracteres`;
      setValidationState('error');
      setValidationMessage(msg);
      onValidationChange?.(false, msg);
      return;
    }

    setValidationState('validating');
    setValidationMessage('Validando...');

    try {
      const result = await productService.validateSku(sku);
      
      if (result.available) {
        setValidationState('available');
        setValidationMessage(result.message);
        onValidationChange?.(true, result.message);
      } else {
        setValidationState('unavailable');
        setValidationMessage(result.message);
        onValidationChange?.(false, result.message);
      }
    } catch (err) {
      console.error('Erro na validação de SKU:', err);
      const msg = 'Erro ao validar SKU';
      setValidationState('error');
      setValidationMessage(msg);
      onValidationChange?.(false, msg);
    }
  }, [maxLength, onValidationChange]);

  // Handle input change with debounce
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);

    // Clear previous timeout
    if (validationTimeout) {
      clearTimeout(validationTimeout);
    }

    // Set new timeout for validation
    const newTimeout = setTimeout(() => {
      // Durante edição, não validamos se o valor não mudou (para evitar erro do próprio SKU)
      if (!isEditing || newValue !== value) {
        validateSku(newValue);
      }
    }, 500); // 500ms de debounce

    setValidationTimeout(newTimeout);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (validationTimeout) {
        clearTimeout(validationTimeout);
      }
    };
  }, [validationTimeout]);

  // Get validation icon
  const getValidationIcon = () => {
    switch (validationState) {
      case 'validating':
        return <Loader2 className="animate-spin text-blue-500" size={18} />;
      case 'available':
        return <Check className="text-green-500" size={18} />;
      case 'unavailable':
        return <AlertCircle className="text-red-500" size={18} />;
      case 'error':
        return <AlertCircle className="text-orange-500" size={18} />;
      default:
        return null;
    }
  };

  // Get validation message color
  const getValidationMessageClasses = () => {
    switch (validationState) {
      case 'validating':
        return 'text-blue-600';
      case 'available':
        return 'text-green-600';
      case 'unavailable':
        return 'text-red-600';
      case 'error':
        return 'text-orange-600';
      default:
        return 'text-slate-500';
    }
  };

  // Get input border color
  const getInputClasses = () => {
    if (error) return ''; // Let the Input component handle error styling

    switch (validationState) {
      case 'available':
        return 'border-green-300 focus:border-green-500 focus:ring-green-200';
      case 'unavailable':
        return 'border-red-300 focus:border-red-500 focus:ring-red-200';
      case 'error':
        return 'border-orange-300 focus:border-orange-500 focus:ring-orange-200';
      default:
        return '';
    }
  };

  return (
    <div>
      <Input
        label={label}
        value={value}
        onChange={handleChange}
        error={error}
        disabled={disabled}
        placeholder={placeholder}
        maxLength={maxLength}
        endIcon={getValidationIcon()}
        className={clsx(getInputClasses())}
      />
      
      {/* Validation message */}
      {validationMessage && !error && (
        <p className={clsx('text-sm mt-1', getValidationMessageClasses())}>
          {validationMessage}
        </p>
      )}
    </div>
  );
};

export default SkuInput;